import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { PencilIcon } from '@heroicons/react/24/outline';
import { GET_DOCUMENT_TEMPLATES } from '../../graphql/queries';
import { Spinner } from '../common/Spinner';
import type { DocType } from '../../constants/documentTemplatePlaceholders';

interface DocumentTemplateRow {
  docType: DocType;
  content: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByName: string;
  updatedBy: string;
  updatedByName: string;
}

/**
 * Format an ISO timestamp as `YYYY-MM-DD` in the Asia/Seoul timezone.
 * We use the `sv-SE` locale because Swedish formats dates as
 * `YYYY-MM-DD` with dashes natively, which is exactly what Req 2.7
 * calls for without pulling in a date library.
 */
const KST_DATE = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const formatKstDate = (iso?: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '-';
  return KST_DATE.format(d);
};

/**
 * 제안서/계약서 데이터 매핑 — admin list page for the two document
 * templates. Lists exactly two rows (auto-seeded by the backend on
 * first read) with author and timestamps, and a `[수정]` action per
 * row that opens the per-template editor.
 *
 * This component is mounted by `App.tsx` inside `AdminLayout` — do not
 * wrap the return here.
 */
const DocumentTemplateListPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();

  const { data, loading, error } = useQuery<{ getDocumentTemplates: DocumentTemplateRow[] }>(
    GET_DOCUMENT_TEMPLATES,
    { fetchPolicy: 'cache-and-network' },
  );

  // Surface load failures via the shared admin toast pattern. Keep the
  // inline error block as a fallback so the page is not silent when the
  // toast is dismissed or missed.
  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  const rows = data?.getDocumentTemplates ?? [];

  return (
    <div className="min-h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-[1200px] mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="hover:text-blue-600 transition-colors"
          >
            {t('common:profileDropdown.adminDashboard')}
          </button>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">
            {t('admin:documentTemplates.sidebarLabel')}
          </span>
        </nav>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 pb-16 space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('admin:documentTemplates.title')}
        </h1>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {error && (
            <div className="px-4 py-6 text-sm text-red-600">{error.message}</div>
          )}

          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:documentTemplates.columnItem')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:documentTemplates.columnCreatedAt')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:documentTemplates.columnUpdatedAt')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:documentTemplates.columnAuthor')}
                </th>
                <th className="px-4 py-3 text-left font-medium" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center">
                    <div className="flex justify-center">
                      <Spinner size="medium" />
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                    -
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.docType} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900 font-medium">
                      {t(`admin:documentTemplates.docType.${row.docType}`, row.docType)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatKstDate(row.createdAt)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatKstDate(row.updatedAt)}</td>
                    <td className="px-4 py-3 text-gray-700">{row.updatedByName || '-'}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/document-templates/${row.docType}`)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50"
                      >
                        <PencilIcon className="w-4 h-4" />
                        {t('admin:documentTemplates.actionEdit')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DocumentTemplateListPage;
